import type { ControlDefinition } from '../types';

const instructions =
  'Observe work activities and conditions to determine if critical controls are in place and properly functioning';

const simple = (
  id: string,
  code: string,
  title: string,
  sourceTitle: string,
  sourceDate: string,
  items: string[],
  displayOrder: number,
  category = title,
): ControlDefinition => ({
  id,
  code,
  title,
  sourceTitle,
  revision: 'Revision 0',
  sourceDate,
  displayOrder,
  category,
  instructions,
  headerFields: [],
  sections: [
    {
      id: 'main',
      items: items.map((text, index) => ({
        id: `${id}_${index + 1}`,
        number: String(index + 1),
        text,
      })),
    },
  ],
});

export const controlDefinitions: ControlDefinition[] = [
  simple(
    'inj_01_mobile_equipment',
    'INJ 01',
    'Mobile Equipment',
    'Critical Control Checklist: Mobile Equipment',
    '01/22/2019',
    [
      'Is the employee qualified and properly licensed to operate the equipment?',
      'Are all critical mobile equipment functions (as highlighted on the pre op inspection) in safe working order?',
      'Are all mobile equipment safety devices functional and being used (e.g., seatbelt, alarm, horn, brakes, etc.)?',
      'Is the employee operating the MEWP/AWP properly tied off as required? When walking a scissor lift, is the employee in a safe working zone?',
      'Is the equipment traveling on a designated path with sufficient clearance to objects and people? Is mobile equipment clear of overhead powerlines?',
      'Does the operator have clear line of sight, observing load and travel path, using mirrors, or using a spotter?',
      'Is the operator maintaining a safe speed, keeping all body parts within, and controlling the equipment/load in a safe manner?',
      'Are appropriate traffic/pedestrian controls in place (e.g., cones, designated loading/unloading area)? Are traffic/pedestrian controls being followed?',
      'When in operation, are the equipment controls being correctly used (e.g., tilt, height, etc.)? When parked, are hydraulics lowered?',
      'Is the equipment appropriate/designed for the task? Is the surface sufficient to prevent risk of tipping, sliding, or slipping, etc.?',
    ],
    1,
  ),
  simple(
    'inj_02_falling_objects',
    'INJ 02',
    'Falling Objects with enough mass and velocity to cause SIF harm',
    'Critical Control Checklist: Falling Objects with enough mass and velocity to cause SIF harm',
    '01/22/2019',
    [
      'Can employees describe how they are qualified to perform tasks involving potential for falling objects? Do they demonstrate proficiency?',
      'Barriers are in place at lower levels to keep personnel from being exposed to overhead objects/loads?',
      'Are employees conducting work out of the line of fire?',
      'Are objects at height properly secured or latched? Are overhead items (equipment, materials, tools, PPE, etc.) properly secured/tethered? Have weather conditions been considered (e.g., wind)?',
      'Lifting/loading devices and storage racks, etc. are properly rated for work being performed or items being stored?',
      'Has communication between work groups been conducted (overhead work or other lifting activities, etc.)?',
      'Has a pre task plan (Take 10) or JSA been conducted and communicated?',
      'Have elimination or guarding controls (e.g., toe rails, rails, barriers, etc.) for falling object hazards been considered and appropriately applied?',
      'Are excessive materials/tools being stored at height?',
    ],
    2,
  ),
  simple(
    'inj_03_loto',
    'INJ 03',
    'Control of Hazardous Energy (Lock Out/Tag Out)',
    'Critical Control Checklist: Control of Hazardous Energy (Lock Out/Tag Out)',
    '01/22/2019',
    [
      'Are the involved employees able to describe how they are qualified and experienced to perform Lock Out for this task?',
      'Are isolation devices and sources of energy clearly labeled (e.g. breaker panels, control valves, wheel chocks, blocking devices, etc.), and easily accessible?',
      'Are involved employees wearing the appropriate PPE for the task?',
      'Can involved employees demonstrate all energy sources have been isolated, locked, tagged, and tested to be in a zero energy state?',
      'Are keys and tags secured properly (e.g., in a lockbox, under control of the worker, etc.)? Is access to lockout keys restricted to the person that locked the equipment out?',
      'Is communication with all potentially affected parties regarding energy isolation and re energization of equipment being conducted?',
      'If energy isolation work lasts longer than one shift, is it controlled appropriately throughout LOTO process?',
    ],
    3,
  ),
  simple(
    'inj_04_caught_struck',
    'INJ 04',
    'Caught In / Struck By',
    'Critical Control Checklist: Caught In / Struck By',
    '01/24/2019',
    [
      'Can the employee summarize the pre task plan associated with this task (e.g., Take 10 card, JSA, etc.)?',
      'Can the employee identify pinch points, crush points, pull in areas, etc. for the task being performed?',
      'Can the employee describe how to remain protected from moving components?',
      'Are wheels chocked on equipment that could move or roll?',
      'Does the employee use a safety lock device on equipment that support hydraulic cylinders to prevent the release of stored energy?',
      'Are all guards in place and properly secured?',
      'Is the employee positioned outside of the Line of Fire?',
      'Does the employee establish an escape route to prevent being pinned between two objects?',
      'Is loose clothing secured when working near rotating components?',
      'Have precautions been taken to keep unauthorized personnel out of the line of fire?',
    ],
    4,
  ),
  simple(
    'inj_05_falls',
    'INJ 05',
    'Falls',
    'Critical Control Checklist: Falls',
    '01/22/2019',
    [
      'Are personnel qualified in task they are performing and/or using fall protection? Do employees demonstrate competency?',
      'Has the work area been assessed for exposures using a Take 10 card and/or JSA (e.g., impaling objects, trip or fall hazards)?',
      'Are fall arrest anchor points approved and high enough to avoid contact with lower level/avoid swing falls?',
      'Have elimination or guarding controls for fall hazards been considered and appropriately applied (e.g., gates, guardrails/handrails, personal fall arrest system)?',
      'Have overhead clearances (objects, ceilings, utilities) been detected, repositioned, de energized, locked out, and field verified?',
      'Have ladders and/or scaffolds been properly inspected and tagged for use?',
      'Are personal fall arrest systems (PFAS) in good condition, properly adjusted/worn with appropriate inspections completed?',
      'Are walking surfaces well lit and maintained? Are walking surfaces free of slip, trip fall hazards (e.g., snow, ice, grease, cables, ropes, raised edges and tools, etc.)?',
      'Are 3 points of control used for entry and exit on all equipment? Are personnel using handrails on stairways?',
      'Are Scaffold E/D personnel using minimum 18 inches of planking to stand on while erecting or dismantling scaffolding?',
      'Are Scaffold E/D personnel utilizing ladders for access and egress on scaffolding with 3 points of contact?',
      'Are personnel maintaining 100% fall protection while at height?',
    ],
    5,
  ),
  {
    id: 'inj_06_electrical',
    code: 'INJ 06',
    title: 'Electrical Hazards/Arc Flash',
    sourceTitle: 'Critical Control Checklist: Electrical Hazards/Arc Flash',
    revision: 'Revision 0',
    sourceDate: '01/24/2019',
    displayOrder: 6,
    category: 'Electrical / Arc Flash / Electrical Hazards',
    instructions,
    headerFields: [],
    sourceNotes: [
      'Rows 8, 10, 11, and 12 are source placeholders. Row 9 is blank in the authoritative source. They are preserved as inactive source rows and are not checklist questions.',
    ],
    sections: [
      {
        id: 'main',
        items: [
          { id: 'inj_06_electrical_1', number: '1', text: 'Are employees familiar with electrical hazards in the shop and in the field, and how to protect themselves from those hazards during use, maintenance, and troubleshooting?' },
          { id: 'inj_06_electrical_2', number: '2', text: 'Are pre task planning documents (e.g., JSA\'s, Job Survey, permits, etc.) in place and used when working with and around electrical hazards?' },
          { id: 'inj_06_electrical_3', number: '3', text: 'Is the correct PPE and testing equipment provided and used while working on, with, or around electrical equipment?' },
          { id: 'inj_06_electrical_4', number: '4', text: 'Is electrical equipment and electrical testing equipment properly designed, maintained and inspected to ensure safe condition and operation? Equipment is labeled to identify arc flash hazard, voltage/amperage, etc.' },
          { id: 'inj_06_electrical_5', number: '5', text: 'Are employees who work on energized equipment trained and proficient on the energized work procedures.?' },
          { id: 'inj_06_electrical_6', number: '6', text: 'Can employees adequately explain emergency procedures in the case of electrocution or arc flash explosion?' },
          { id: 'inj_06_electrical_7', number: '7', text: 'Are employees trained, qualified, and do they know how to perform lock out and tag out? (See CC Checklist on Lock Out/Tag Out)' },
          { id: 'inj_06_electrical_8', number: '8', text: 'Click or tap here to enter text.', inactive: true, sourceNote: 'Source placeholder, not a checklist question.' },
          { id: 'inj_06_electrical_9', number: '9', text: '', inactive: true, sourceNote: 'Blank source row, not a checklist question.' },
          { id: 'inj_06_electrical_10', number: '10', text: 'Click or tap here to enter text.', inactive: true, sourceNote: 'Source placeholder, not a checklist question.' },
          { id: 'inj_06_electrical_11', number: '11', text: 'Click or tap here to enter text.', inactive: true, sourceNote: 'Source placeholder, not a checklist question.' },
          { id: 'inj_06_electrical_12', number: '12', text: 'Click or tap here to enter text.', inactive: true, sourceNote: 'Source placeholder, not a checklist question.' },
        ],
      },
    ],
  },
  simple(
    'inj_07_temperature',
    'INJ 07',
    'Temperature Extremes',
    'Critical Control Checklist: Temperature Extremes',
    '01/24/2019',
    [
      'When employees will be working in extreme ambient temperatures (high or low), was the weather forecast checked or work area temperature checked to determine if high heat or cold temperature controls are needed?',
      'Proper clothing is worn for the temperature and proper PPE is worn for the task being completed.',
      'Adequate cool, potable water is available and in sufficient quantities and within close proximity for all crewmembers (one quart or more per person per hour).',
      'Is adequate shelter available as close as practicable to the areas where employees are working and large enough to accommodate the number of employees present.',
      'Do employee(s) understand(s) the need to take frequent/periodic breaks? Is the foreman reminding crew members to take breaks, drink water, and rotating tasks as needed?',
      'Can crew members recognize signs of heat or cold related illness and are trained first aid/CPR personnel available?',
      'Are cooling / warming methods available (e.g. vehicle).',
      'Are crewmembers communicating with each other regarding heat and cold breaks?',
      'Are employees able to demonstrate they are qualified/experienced to complete tasks involving extreme temperatures (e.g., welding or filling propane)?',
      'Is proper PPE worn while working with high or low temperature extremes (e.g., welding or filling propane)',
    ],
    7,
  ),
  simple(
    'inj_08_hazardous_release',
    'INJ 08',
    'Hazardous Release',
    'Critical Control Checklist: Hazardous Release',
    '01/24/2019',
    [
      'Can employees describe the properties and potential exposures to hazardous materials on site (SDS\'s available, understood, and applied when necessary)?',
      'Are Job Safety Analyses (JSA\'s) in place and used when working with hazardous chemicals (including propane, fuels, solvents, cleaners, etc.)?',
      'Are compressed gases, liquids, sprays, and other inhalant or skin contact hazards used with proper PPE and in accordance with procedures for the materials, including use in well ventilated areas if required?',
      'Are critical systems containing hazardous materials properly designed, maintained, inspected and labeled (per the SDS) to ensure safe condition and operation?',
      'Are critical systems containing hazardous materials operated only by qualified employees demonstrating proficiency in their tasks?',
      'Are equipment returns confirmed to be free of hazardous chemicals/materials prior to receipt, off loading and handling?',
      'Are wash bays designed and operated to contain potentially hazardous materials and provide for safe handling and disposal?',
      'Are hazardous chemicals and materials stored in safe areas in proper containers with required labeling? Are drum and tank storage areas designed to contain spills?',
      'Is combustion equipment (using natural gas, propane, diesel, etc.) properly maintained and ventilated to prevent carbon monoxide exposures?',
      'Is natural gas and propane equipment maintained, inspected, and used according to manufacturer\'s specifications? Are shop equipment and supply lines protected from impact and damage that could cause breaches or leaks?',
      'Are emergency response plans in place and available to ensure that employees are prepared? Are evacuation drills conducted twice annually?',
      'Can employees describe their responsibilities if a spill or release occurs? Are containment and response supplies available, including PPE appropriate for the hazardous materials being stored or used?',
    ],
    8,
  ),
  simple(
    'inj_09_lifting_rigging',
    'INJ 09',
    'Lifting and Rigging Non Critical Lift',
    'Critical Control Checklist: Lifting and Rigging Non Critical Lift',
    '01/24/2019',
    [
      'Can employees describe how they are qualified to perform tasks involving lifting/rigging?',
      'Did the employees complete a pre task plan (Take 10 card or JSA)?',
      'Are employees using proper PPE (Gloves, Safety Glasses, Hard Hat, etc.)?',
      'Is the annual crane/hoist inspection current? (Is it documented) Has the daily visual inspection been completed? (Lifting Equipment)',
      'Are safety devices installed and tested?',
      'Is the wind speed below 30mph? (Wind speed >20mph require reassessment of Work Risk, if wind speed is greater than 30mph stop)',
      'Have precautions been taken to keep other personal out of the area? Was the need to protect the swing area and lift/landing zones considered?',
      'Has stability of the lifting device been assessed, and is it adequate for this lift (e.g., properly anchored, ground stability, etc.)?',
      'Has the rigging been inspected? (shackles, chains, wire, rope, etc.)',
      'Is the lifting device and rigging proper capacity?',
      'Has the need for taglines been evaluated and is the load to be lifted stable?',
    ],
    9,
    'Lifting & Rigging',
  ),
  simple(
    'inj_10_power_tools',
    'INJ 10',
    'Power Tools',
    'Critical Control Checklist: Power Tools',
    '01/22/2019',
    [
      'Can the worker describe how they know they have the right tool for this task?',
      'Can the worker describe how they are qualified and experienced to perform this task?',
      'Can the worker demonstrate knowledge using the tool, including de energizing the power source when replacing media or servicing the tool?',
      'Are all manufacturer guards in place and functioning?',
      'Has the worker prepared the area and positioned their body so they and others are out of the line of fire (cutting edge, entanglement, etc.)?',
      'Has the tool been inspected prior to use; is in safe and good operating condition?',
      'Is the electrical tool grounded or double insulated and in good condition?',
      'Are valves, fittings, filters, and hoses in good working condition and not leaking?',
      'Is the worker properly positioned when starting/testing liquid fuel powered tools?',
    ],
    10,
  ),
  simple(
    'inj_11_bites_attacks',
    'INJ 11',
    'Bites & Attacks (Insects & Animals)',
    'Critical Control Checklist: Bites & Attacks (Insects & Animals)',
    '02/12/2019',
    [
      'When encountering a threatening or unknown dog, employees utilize the "tree" position by standing still, folding arms and looking straight down.',
      'To prevent snake bites, employees wear long pants and high, "over the ankle" work boots, and use noise makers (air horns, bells or shakers) when walking through heavily wooded areas or tall grass where snakes may be present.',
      'When encountering wasps, hornets or bees, employees remain calm and move away slowly. Use sprays to remove them if area access is required.',
      'In tick prone areas, employees cover exposed skin (use long sleeves, trousers, etc.) and use insect repellent on exposed skin. Repellents that contain 50% DEET (or other tick repellent) are most effective.',
      'When in or around areas that could house spiders/wasps, employees wear gloves and long sleeves.',
      'Employees with known allergies to stings or bites (bee stings, for example) have appropriate response supplies available, such as an EpiPen.',
    ],
    11,
    'Bites & Attacks',
  ),
  simple(
    'inj_12_work_permits',
    'INJ 12',
    'Safe Work Permits',
    'Critical Control Checklist: Safe Work Permits',
    '02/21/2019',
    [
      'Can the employee describe when and where safe work permits are required at the facility or job location (e.g. confined space, hot work, etc.)?',
      'Can the employee describe other specific, uncommon situations when a confined space hazard may be present (e.g. a water truck tanker, infrequent manhole or trench entry, etc.)?',
      'Can the employee identify designated areas at the profit center where hot work activities do not require a hot work permit (e.g. part of the shop, yards, etc.)?',
      'Are permits completed properly by qualified people, and are qualified personnel assigned to complete the work? And how are they qualified?',
      'If on customer site, have Sunbelt employees located and read customer safe work permit(s)?',
      'If on customer site and using customer permit, have Sunbelt employees verified the scope and location of the work, and have the employees performed an inspection of the work location with the Sunbelt team and the permit issuer.',
      'If on customer site and using customer permits, are the employees complying with customer permit requirements?',
    ],
    12,
    'Work Permits',
  ),
  simple(
    'inj_13_fires',
    'INJ 13',
    'Fires',
    'Critical Control Checklist: Fires',
    '01/22/2019',
    [
      'Has the employee indicated that Hot Work will take place?',
      'Are the fire extinguishers fully charged, inspected monthly by the PC and inspected/tagged annually by vendor?',
      'Are fire extinguishers securely mounted at proper height, visible signage to identify location and clear of obstructions?',
      'Can the employee explain the proper operation of fire extinguishers?',
      'In event of fire, is employee able to identify and explain evacuation procedures and muster points?',
      'Can employee identify their nearest fire extinguisher?',
      'Can the employee identify the location of the emergency fuel shut off button?',
      'Does the employee adhere to correct storage and handling of flammable materials?',
      'Can the employee servicing equipment demonstrate proper inspection of fuel and electrical systems in accordance with the Service Maintenance Form? Or, can the operator demonstrate proper prestart inspection procedure?',
    ],
    13,
  ),
  simple(
    'inj_14_loading_unloading',
    'INJ 14',
    'Loading/Unloading',
    'Critical Control Checklist: Loading/Unloading',
    '02/12/2019',
    [
      'Is the employee trained on the loading equipment and any mobile equipment being loaded, and trained on the loading/unloading process?',
      'Has the work area been assessed for exposures using a Take 10 card and/or JSA?',
      'Are all critical loading and mobile equipment functions (brakes, operator controls, etc.) in safe working order?',
      'Is the loading equipment appropriate/designed for the task? Is it being used to control exposure (e.g. winch, ramps, lift gate, roll back, hoist, etc.)?',
      'Is the employee wearing proper personal fall protection and properly tied off as required (AWP/MEWP)? Is the employee wearing a seatbelt (forklift)?',
      'Are wheels chocked during the load/unload process?',
      'Are trailer surfaces well maintained? Is the trailer surface level, free of slip, trip fall hazards (e.g., snow, ice, grease, cables, raised edges, etc.)?',
      'Are appropriate traffic/pedestrian controls in place (e.g., cones, designated loading/unloading area)? Are traffic/pedestrian controls being followed?',
      'Are employees conducting work out of the line of fire, (e.g. avoiding caught between hazards, walking a scissor lift, working from the ground, etc.)',
      'Does the operator control the equipment/load in a safe manner, maintaining clear line of sight or using a spotter, maintaining safe speed, observing load and travel path, etc.?',
      'Are 3 points of control and other safe practices used for entry and exit on all equipment, especially equipment loaded on trailers for transport?',
      'Is loaded material and equipment properly secured to prevent movement while vehicle is in place and during transport?',
    ],
    14,
  ),
  {
    id: 'mvi_01_amplified',
    code: 'MVI 01',
    title: 'MVI Amplified',
    sourceTitle: 'Critical Control Checklist: MVI Amplified',
    revision: 'Revision 02/22/2026',
    sourceDate: '02/22/2026',
    displayOrder: 15,
    category: 'MVI / Amplified Driving Critical Control',
    instructions,
    headerFields: [
      { id: 'driver', label: 'Driver', required: true },
      { id: 'observer', label: 'Observer', required: true },
    ],
    sections: [
      {
        id: 'pre_post_trip',
        title: 'Pre and Post Trip',
        items: [
          { id: 'mvi_01_1', number: '1', text: 'Team Member Preparedness: Team member is fit for duty and familiar with the vehicle and completes necessary communications prior to departure.' },
          { id: 'mvi_01_2', number: '2', text: 'Vehicle Readiness: Team Member completes a walk around inspection of vehicle before and after each trip; vehicle is chocked and coned as required.' },
          { id: 'mvi_01_3', number: '3', text: 'In Cab Safety: Team member fastens their seatbelt, follows hands free policy, and completes a driving plan prior to departure.' },
          { id: 'mvi_01_4', number: '4', text: 'Load Securement: Team member secures cargo with securement methods specific to vehicle and cargo.' },
          { id: 'mvi_01_5', number: '5', text: 'Towing: Team member inspects all connection components and verifies compatible parts between the power unit and the towable.' },
        ],
      },
      {
        id: 'basic_vehicle_control',
        title: 'Basic Vehicle Control',
        items: [
          { id: 'mvi_01_6', number: '6', text: 'Turning: Team member uses outside turn lanes and turns at the vehicles pivot points.' },
          { id: 'mvi_01_7', number: '7', text: 'Backing Awareness: Team member uses the GOAL method and marks stationary or fixed hazards with road cones. Team member avoids backing when possible.' },
          { id: 'mvi_01_8', number: '8', text: 'Backing Technique: Team member uses mirrors, cameras, back up alarms and horns to see and be seen while backing.' },
          { id: 'mvi_01_9', number: '9', text: 'Braking: Team member applies brakes in a smooth and controlled fashion.' },
          { id: 'mvi_01_10', number: '10', text: 'Merging/Changing Lanes: Team member uses turn signals and mirrors to identify hazards and perform lane changes.' },
          { id: 'mvi_01_11', number: '11', text: 'Wedge Watch Wait: Wedge Team member performs the wedge, watch and wait in traffic as applicable.' },
        ],
      },
      {
        id: 'on_road_skills',
        title: 'On Road Skills',
        items: [
          { id: 'mvi_01_12', number: '12', text: 'Speed and Space Management: Team member maintains speed appropriate for conditions and within posted limits.' },
          { id: 'mvi_01_13', number: '13', text: 'Following Time: Team member maintains following time of 3 to 5 seconds for non commercial or 5 to 7 seconds for commercial vehicles.' },
          { id: 'mvi_01_14', number: '14', text: 'Scanning: Team member scans mirror every 5 to 8 seconds, scans ahead 12 to 15 seconds.' },
          { id: 'mvi_01_15', number: '15', text: 'Traffic Signs and Signals: Team member obeys all traffic signs and signals, uses good judgement on yellow lights, calls out stale vs fresh lights.' },
        ],
      },
    ],
  },
];

export const getControlDefinition = (id: string) =>
  controlDefinitions.find((control) => control.id === id);
